import { Dropdown } from 'antd';
import * as React from 'react';
import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { StatusField, StatusFieldOption, TaskType } from 'api/tasks';
import { ClassNamePropsWithChildren } from 'components/ozone-component';
import { FieldHelper } from 'utils/field';
import { StatusPickerContext } from './context';
import { StatusPickerOverlay } from './status-picker-overlay';
import { StatusPickerTrigger } from './trigger';

interface StatusPickerProps extends ClassNamePropsWithChildren {
  dropdownHeader: string | ReactNode;
  value?: number | string; // NOTE:: Unify this once project phase status is changed as well.
  onChange?: (selected: StatusFieldOption) => void;
  attachTo?: HTMLElement | null;
  type?: TaskType;
  disabled?: boolean;
  field: StatusField;
}

const StatusPicker: FC<StatusPickerProps> & StatusPickerComposition = ({
  value,
  onChange,
  children,
  className,
  dropdownHeader,
  attachTo,
  type = TaskType.Task,
  disabled = false,
  field,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const {
    options: { activeOptions, allOptions },
  } = FieldHelper(field);
  const selectedValue = allOptions.find(
    (option) => option.value === value,
  ) as StatusFieldOption;
  const [selected, setSelected] = useState<StatusFieldOption | undefined>();

  useEffect(() => {
    setSelected(selectedValue);
  }, [selectedValue]);

  const handleOnChange = (selectedOption: StatusFieldOption) => {
    if (onChange) {
      onChange(selectedOption);
    }
    setSelected(selectedOption);
    setIsOpen(false);
  };

  const ref = useRef<HTMLDivElement>(null);

  return (
    <StatusPickerContext.Provider
      value={{ selected, isOpen, toggle: setIsOpen, type }}
    >
      <Dropdown
        className={className}
        visible={isOpen}
        onVisibleChange={setIsOpen}
        getPopupContainer={attachTo ? () => attachTo : undefined}
        overlay={
          <StatusPickerOverlay
            header={dropdownHeader}
            onChange={handleOnChange}
            options={activeOptions as StatusFieldOption[]}
            value={selected!}
          />
        }
        trigger={['click']}
        disabled={disabled}
      >
        <div ref={ref} role="presentation" onClick={(e) => e.stopPropagation()}>
          {children}
        </div>
      </Dropdown>
    </StatusPickerContext.Provider>
  );
};

interface StatusPickerComposition {
  Trigger: typeof StatusPickerTrigger;
}
StatusPicker.Trigger = StatusPickerTrigger;

export { StatusPicker };
